import React, { useEffect } from 'react';
import { TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import {
  Button,
  RadioGroup,
  SheetContent,
  SheetModal,
  SheetModalTrigger,
  SvgIcon,
  Text,
} from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

import { PlayerSortKey, PlayerSortOrder, usePlayerSort } from '../context/player-sort-context';

export type PlayerSortSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sortButtonLabel: string;
  closeWithoutSave: () => void;
  saveAndClose: () => void;
  levelFilter: number | null;
  saved: { key: PlayerSortKey; order: PlayerSortOrder };
  draft: { key: PlayerSortKey; order: PlayerSortOrder };
  setDraft: (next: { key: PlayerSortKey; order: PlayerSortOrder }) => void;
};

export function PlayerSortSheet({
  open,
  onOpenChange,
  sortButtonLabel,
  closeWithoutSave,
  saveAndClose,
  levelFilter,
  saved,
  draft,
  setDraft,
}: PlayerSortSheetProps) {
  const styles = useStyles();
  const { key: sortKey } = usePlayerSort();
  const { scale, deviceWidth, deviceHeight, isTablet } = useScaling();
  const isLandscape = deviceWidth > deviceHeight;
  const disableLevelSort = levelFilter != null;

  // Open sheet = start editing from saved sort.
  useEffect(() => {
    if (!open) return;
    // If a level filter is selected, never allow level sort in draft.
    if (disableLevelSort && saved.key === 'level') {
      setDraft({ key: 'name', order: 'asc' });
      return;
    }
    setDraft({ key: saved.key, order: saved.order });
  }, [disableLevelSort, open, saved.key, saved.order, setDraft]);

  const onChangeOrder = (key: PlayerSortKey, order: PlayerSortOrder) => {
    setDraft({ key, order });
  };

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetModalTrigger>
        <Button style={styles.sortButton}>
          <Button.Icon name="IcSort" size={scale(24)} />
          <Button.Text variant="Font Inter/Subtitle/Regular 12px" style={styles.sortButtonText}>
            {sortButtonLabel}
            {sortKey && <Text fontWeight={'700'}>: {sortKey}</Text>}
          </Button.Text>
        </Button>
      </SheetModalTrigger>

      <SheetContent tabletWidth={scale(375)} closeOnBackdropPress={false} style={styles.sheet}>
        <Stack direction="column" style={styles.sheetContainer}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity
              onPress={closeWithoutSave}
              hitSlop={scale(12)}
              style={styles.backButton}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={scale(24)} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.player.sort.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.title}>
                {STRINGS.player.sort.sheetTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerRightSpacer} />
          </Stack>

          <Stack style={styles.sheetBodyContent}>
            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 12px'
                  : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
              }
              fontWeight={isTablet ? undefined : '800'}
              style={styles.section}>
              {STRINGS.player.sort.sectionByFirstName}
            </Text>
            <RadioGroup
              direction={isLandscape ? 'column' : 'row'}
              value={draft.key === 'name' ? draft.order : null}
              onValueChange={v => onChangeOrder('name', v as PlayerSortOrder)}>
              <RadioGroup.Radio value="asc" label={STRINGS.player.sort.ascending} />
              <RadioGroup.Radio value="desc" label={STRINGS.player.sort.descending} />
            </RadioGroup>

            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 12px'
                  : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
              }
              fontWeight={isTablet ? undefined : '800'}
              style={[styles.section, styles.sectionSpacing]}>
              {STRINGS.player.sort.sectionByLevel}
            </Text>
            <RadioGroup
              direction={isLandscape ? 'column' : 'row'}
              disabled={disableLevelSort}
              value={draft.key === 'level' ? draft.order : null}
              onValueChange={v => onChangeOrder('level', v as PlayerSortOrder)}
              style={disableLevelSort ? styles.disabledGroup : undefined}>
              <RadioGroup.Radio value="asc" label={STRINGS.player.sort.ascending} />
              <RadioGroup.Radio value="desc" label={STRINGS.player.sort.descending} />
            </RadioGroup>

            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 12px'
                  : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
              }
              fontWeight={isTablet ? undefined : '800'}
              style={[styles.section, styles.sectionSpacing]}>
              {STRINGS.player.sort.sectionByAge}
            </Text>
            <RadioGroup
              direction={isLandscape ? 'column' : 'row'}
              value={draft.key === 'age' ? draft.order : null}
              onValueChange={v => onChangeOrder('age', v as PlayerSortOrder)}>
              <RadioGroup.Radio value="asc" label={STRINGS.player.sort.ascending} />
              <RadioGroup.Radio value="desc" label={STRINGS.player.sort.descending} />
            </RadioGroup>

            <Stack style={isLandscape ? styles.sheetFooterLandscape : styles.sheetFooterPortrait}>
              <Button onPress={saveAndClose} style={styles.doneButton}>
                <Button.Text>{STRINGS.player.sort.done}</Button.Text>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  sortButtonText: {
    textTransform: 'uppercase',
    paddingHorizontal: scale(0),
    paddingVertical: scale(0),
  },
  sortButton: {
    height: scale(!isTablet ? 32 : 40),
    flex: !isTablet ? 1 : undefined,
    width: isTablet ? scale(140) : undefined,
    borderRadius: scale(4),
    paddingHorizontal: scale(0),
    paddingVertical: scale(0),
  },
  sheet: {
    backgroundColor: 'white',
  },
  sheetContainer: {
    flex: 1,
  },
  sheetHeader: {
    paddingHorizontal: scale(20),
    paddingTop: scale(35),
    marginBottom: scale(40),
  },
  backButton: {
    width: scale(45),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  title: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  headerRightSpacer: {
    width: scale(45),
  },
  sheetBodyContent: {
    flex: 1,
    paddingHorizontal: scale(20),
    // paddingBottom: scale(16),
    gap: scale(12),
  },
  section: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  sectionSpacing: {
    marginTop: scale(12),
  },
  disabledGroup: {
    opacity: 0.5,
  },
  sheetFooterPortrait: {
    paddingTop: scale(32),
    paddingBottom: scale(16),
  },
  sheetFooterLandscape: {
    paddingTop: scale(32),
    justifyContent: 'flex-end',
    flex: 1,
  },
  doneButton: {
    width: '100%',
    height: scale({ phone: 40, tablet: 48 }),
  },
}));
